import { useState } from "react";
import axios from "axios";
import { Receipt, Loader2, Download } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { Reveal } from "../../components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

export default function Receipts() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/payments/lookup`, { email: email.trim(), phone: phone.trim() });
      setResults(data.payments);
    } catch (err) {
      setError(err?.response?.data?.detail || "Lookup failed — please try again.");
      setResults(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased min-h-screen" data-testid="receipts-page">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-5 pt-36 pb-20">
        <Reveal>
          <p className="text-[10px] tracking-[0.26em] uppercase text-nx-goldlight">Payments</p>
          <h1 className="font-marcellus text-4xl text-white mt-2">Your payment receipts</h1>
          <p className="text-sm text-slate-400 mt-3 leading-relaxed">
            Enter the email address and phone number you paid with. We will list your completed payments so you can view or print any receipt again.
          </p>
        </Reveal>
        <Reveal>
          <form onSubmit={lookup} className="mt-8 border border-white/10 bg-white/[0.02] p-6 sm:p-8 flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required type="email" placeholder="Email used while paying" className={input} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="receipts-email" />
              <input required type="tel" placeholder="Phone used while paying" className={input} value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="receipts-phone" />
            </div>
            <button type="submit" disabled={busy} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-8 py-4 transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="receipts-lookup">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Receipt size={15} />}
              {busy ? "Looking up…" : "Find my receipts"}
            </button>
            {error && <p className="text-sm text-red-400" data-testid="receipts-error">{error}</p>}
          </form>
        </Reveal>
        {results && (
          <Reveal>
            <div className="mt-8" data-testid="receipts-results">
              {results.length === 0 ? (
                <p className="text-sm text-slate-400 border border-white/10 bg-white/[0.02] p-6">No completed payments found for this email and phone combination. Check the details and try again, or write to info@nexsecure.in.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {results.map((p, i) => (
                    <div key={i} className="border border-white/10 bg-white/[0.02] p-5 flex flex-wrap items-center gap-4 justify-between" data-testid={`receipt-row-${i}`}>
                      <div>
                        <p className="text-white text-sm">{p.reference}</p>
                        <p className="text-xs text-slate-500 mt-1">{p.paid_at} · {p.gateway} · ₹{p.amount}</p>
                      </div>
                      <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-nx-gold/50 text-nx-goldlight hover:bg-nx-gold/10 text-xs font-semibold tracking-[0.12em] uppercase px-5 py-2.5 transition-colors" data-testid={`receipt-download-${i}`}>
                        <Download size={13} /> Receipt
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
